// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getAdminProfile, updateAdminProfile } from '../../feature/actionCreators';
import { adminProfileSelector } from '../../feature/selectors';

// Components
import { AdminProfileForm } from '../../components';
import {
  StyledButtonsWrap,
  StyledButton,
  StyledContentBlock,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledPageWrapper,
} from '../AdminProfileContainer/AdminProfileContainerStyles';
import ChangePasswordContainer from '../ChangePasswordContainer/ChangePasswordContainer';
import ProfileInfo from '../../components/ProfileInfo/ProfileInfo';
import { CrossIcon, EditIcon } from '@/icons';
import { showSuccessToast } from '@/components';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { TAdminProfileFormValues } from '../../helpers/types';
import AdminProfileDTO from '../../dtos/AdminProfileDTO';
import { useBoolean } from '@/hooks';

const AdminProfileContainer = () => {
  const dispatch = useAppDispatch();
  const { isLoading, profile } = useAppState(adminProfileSelector);
  const { t } = useTranslation(['profile']);
  const { value: isEditMode, setFalse: turnOffEditing, setTrue: turnOnEditing } = useBoolean();
  const { canManage } = usePermissions();

  const canManageProfile = canManage(Resource.PROFILE);

  const handleSubmit = useCallback(async (values: TAdminProfileFormValues) => {
    const adminProfileDto = new AdminProfileDTO(values);
    await dispatch(updateAdminProfile(adminProfileDto)).unwrap();
    showSuccessToast({ message: t('profile_data_saved') });
  }, []);

  useEffect(() => {
    dispatch(getAdminProfile());
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('profile_title')}</StyledPageTitle>
        {canManageProfile &&
          (!isEditMode ? (
            <StyledButtonsWrap>
              <StyledButton onClick={turnOnEditing}>
                <EditIcon />
                {t('profile_edit')}
              </StyledButton>
            </StyledButtonsWrap>
          ) : (
            <StyledButtonsWrap>
              <StyledButton onClick={turnOffEditing}>
                <CrossIcon />
                {t('profile_edit_cancel')}
              </StyledButton>
            </StyledButtonsWrap>
          ))}
      </StyledHeaderBlock>
      <StyledContentBlock>
        {canManageProfile && isEditMode ? (
          <>
            <AdminProfileForm handleSubmit={handleSubmit} isLoading={isLoading} initialValues={profile} />
            <ChangePasswordContainer />
          </>
        ) : (
          <ProfileInfo profileData={profile} />
        )}
      </StyledContentBlock>
    </StyledPageWrapper>
  );
};

export default AdminProfileContainer;
